import React, { useState, useEffect } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { doCreateUserWithEmailAndPassword } from '../../Firebase/auth';
import { useAuth } from '../../Contexts/AuthContext/AuthContext';
import './Signup.css'; 
import { linkVolunteerToFirebase, fetchApplicationData as fetchApplicationDataAPI } from '../../services/api';

const SignUp = () => {
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('applicationId'); // Get application ID from URL
  const { userLoggedIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [applicationData, setApplicationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch application data on component mount
  useEffect(() => {
    if (applicationId) {
      fetchApplicationData(applicationId);
    }
  }, [applicationId]);

  const fetchApplicationData = async (id) => {
  try {
    console.log('Fetching application data for ID:', id);
    const data = await fetchApplicationDataAPI(id);
    console.log('Application data received:', data);
    
    if (data.application_status === 'approved') {
      console.log('Application is approved, proceeding with signup');
      setApplicationData(data);
      setEmail(data.email);
    } else {
      console.log('Application not approved, status:', data.application_status);
      setErrorMessage('Your application has not been approved yet');
    }
  } catch (error) {
    console.error('Error fetching application:', error);
    setErrorMessage('Error fetching application data. Please try again.');
  }
};

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    if (password !== confirmPassword) {
      setErrorMessage("Passwords do not match");
      setIsLoading(false);
      return;
    }

    if (!applicationData || applicationData.application_status !== 'approved') {
      setErrorMessage('Your application must be approved before signing up');
      setIsLoading(false);
      return;
    }

    try {
      // Create Firebase user
      const userCredential = await doCreateUserWithEmailAndPassword(email, password);
      
      // Link volunteer to Firebase USING EMAIL (not applicationId)
      await linkVolunteerToFirebase(email, userCredential.user.uid);
      
      // Redirect to volunteer dashboard
      window.location.href = '/volunteer';
    } catch (error) {
      console.error('Signup error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setErrorMessage('This email is already registered. Please login instead.');
      } else if (error.code === 'auth/weak-password') {
        setErrorMessage('Password should be at least 6 characters');
      } else {
        setErrorMessage(error.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (userLoggedIn) {
    return <Navigate to="/volunteer" />;
  }

  if (!applicationId) {
    return (
      <div className="signup-page">
        <div className="signup-container">
          <h2>Sign Up Error</h2>
          <p>You need an approved application to sign up. Please apply first.</p>
          <Link to="/application">Go to Application Form</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-page">
      <div className="signup-container">
        <h2 className="signup-heading">Complete Your Registration</h2>
        <div className="signup-card"> 
          <h2>Create Your Account</h2>
          {errorMessage && <p className="error-message">{errorMessage}</p>}
          <form className="signup-form" onSubmit={onSubmit}>
            <div>
              <label>Email:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={applicationData} // Disable if pre-filled from application
              />
            </div>
            <div>
              <label>Password:</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <div>
              <label>Confirm Password:</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            <button 
              type="submit" 
              className="signup-button"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Complete Registration'}
            </button>
          </form>
          <p className="login-link">
            Already have an account? <Link to="/login">Login here</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;